import { LoadingSmall } from "@/components/LoadingSmall";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { getArea, getPlayer } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getLayoutedElements, getNodes, getEdges } from "@/pages/area/AreaPageUtils";
import { AreaPageFlow } from "@/pages/area/AreaPageFlow";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export const PlayerAreaSelect: React.FC<{
  onChange: (intId: number) => void;
  value?: number;
  colorScheme?: 'blue' | 'purple' | 'green' | 'amber' | 'default';
}> = ({ onChange, value, colorScheme = 'default' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const activeFirm = useSelector(selectActiveFirm);
  const { data, isLoading } = useQuery({
    queryKey: ["area", activeFirm?.firmGuid],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
  });

  const { data: player, isLoading: isPlayerLoading } = useQuery({
    queryKey: ["getPlayer", activeFirm?.firmGuid],
    queryFn: () =>
      getPlayer({ pageNumber: 1, numberOfObjectsPerPage: 999999 }, { Firm_Id: String(activeFirm?.firmGuid) }),
  });

  const areaData = data?.data?.result || [];
  const playerData = player?.data?.result || [];

  if (isLoading || isPlayerLoading) return <LoadingSmall />;

  const { nodes, edges } = getLayoutedElements(getNodes(areaData, playerData), getEdges(areaData, playerData));
  const selectedArea = areaData.find((a) => a.intId === value);

  // Color scheme styles
  const getButtonStyles = () => {
    switch (colorScheme) {
      case 'blue':
        return "border-blue-200 hover:border-blue-300 text-blue-700 hover:bg-blue-50";
      case 'purple':
        return "border-purple-200 hover:border-purple-300 text-purple-700 hover:bg-purple-50";
      case 'green':
        return "border-green-200 hover:border-green-300 text-green-700 hover:bg-green-50";
      case 'amber':
        return "border-amber-200 hover:border-amber-300 text-amber-700 hover:bg-amber-50";
      default:
        return "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button 
          type="button" 
          variant="outline"
          className={cn(colorScheme !== 'default' && getButtonStyles())}
        >
          {selectedArea ? selectedArea.name : "-"}
          {selectedArea && selectedArea.overwriteSub ? (
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className="w-2 h-2 text-destructive" />
              </TooltipTrigger>
              <TooltipContent>{t("common.overwriteSubDesc")}</TooltipContent>
            </Tooltip>
          ) : (
            ""
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] h-[90vh]">
        <DialogHeader>
          <DialogTitle>{t("entities.area")}</DialogTitle>
          <DialogDescription className="max-w-[90vw] h-[90vh]">
            <AreaPageFlow
              layoutedNodes={nodes}
              layoutedEdges={edges}
              mode="view"
              onNodeClick={(id) => {
                onChange(id);
                setOpen(false);
              }}
              selectedAreaId={value}
            />
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
